import React from 'react';
import type { TourStep } from './types';

/**
 * Сквозной workflow-тур "Полный цикл: от дизайна до WB"
 * 
 * Этапы:
 * 1. Дизайны (designs) - создание/импорт макетов
 * 2. Категории (categories) - выбор категории для товаров
 * 3. Товары (products) - создание товаров на основе дизайна
 * 4. Пути изображений (media-mappings) - настройка путей к картинкам
 * 5. Wildberries (wildberries) - выгрузка товаров
 */
export const fullCycleSteps: TourStep[] = [
  // ==========================================
  // ЭТАП 1: ВВЕДЕНИЕ (на странице дизайнов)
  // ==========================================
  {
    target: 'body',
    title: 'Добро пожаловать в полный цикл!',
    content: (
      <div>
        <p className="mb-3">
          Этот тур проведет вас через весь процесс от создания дизайна до выгрузки товара на Wildberries.
        </p>
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
          <p className="text-sm font-medium text-indigo-800 mb-2">Вы научитесь:</p>
          <ol className="text-sm text-indigo-700 list-decimal pl-4 space-y-1">
            <li>Создавать дизайн-макеты</li>
            <li>Выбирать категории для товаров</li>
            <li>Создавать товары на основе дизайнов</li>
            <li>Настраивать пути к изображениям</li>
            <li>Выгружать товары на Wildberries</li>
          </ol>
        </div>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    navigateTo: 'designs',
  },

  // ==========================================
  // ЭТАП 2: ДИЗАЙНЫ - Создание макета
  // ==========================================
  {
    target: '[data-tour="designs-create-btn"]',
    title: 'Шаг 1: Создание дизайна',
    content: (
      <div>
        <p className="mb-2">
          Всё начинается с дизайна! Нажмите <strong>«Создать макеты»</strong>, чтобы добавить новый дизайн.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm">
          <strong>Совет:</strong> Также можно импортировать много дизайнов сразу через выпадающее меню.
        </div>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    navigateTo: 'designs',
  },
  {
    target: '[data-tour="designs-tabs"]',
    title: 'Комплектность дизайнов',
    content: (
      <div>
        <p className="mb-2">
          После создания дизайна важно следить за <strong>комплектностью</strong>.
        </p>
        <p className="text-sm text-gray-600">
          Вкладка «Неполный комплект» покажет дизайны, для которых ещё не созданы все товары.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    navigateTo: 'designs',
  },
  {
    target: '[data-tour="designs-completeness-header"]',
    title: 'Статус комплекта',
    content: (
      <div>
        <p className="mb-2">
          В колонке «Статус комплекта» показан прогресс создания товаров для каждого дизайна.
        </p>
        <div className="flex items-center gap-2 text-sm">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-800">Полный</span>
          <span>— все товары созданы</span>
        </div>
        <div className="flex items-center gap-2 text-sm mt-1">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">2/5</span>
          <span>— создано 2 из 5 товаров</span>
        </div>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
    navigateTo: 'designs',
  },

  // ==========================================
  // ЭТАП 3: КАТЕГОРИИ - Выбор категории
  // ==========================================
  {
    target: 'body',
    title: 'Переходим к категориям',
    content: (
      <div>
        <p className="mb-2">
          Отлично! Дизайн создан. Теперь нужно понять, в какой <strong>категории</strong> создавать товары.
        </p>
        <p className="text-sm text-gray-600">
          Категории определяют тип товара и его характеристики (вес, размер, цена и т.д.)
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    navigateTo: 'categories',
  },
  {
    target: '[data-tour="categories-list"]',
    title: 'Список категорий',
    content: (
      <div>
        <p className="mb-2">
          Здесь представлены все доступные категории товаров.
        </p>
        <p className="text-sm text-gray-600 mb-2">
          Каждая категория имеет свои настройки: префикс артикула, состав, сертификаты, размеры упаковки.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-sm">
          <strong>Например:</strong> Кружки, Футболки, Подушки — это разные категории с разными характеристиками.
        </div>
      </div>
    ),
    placement: 'top',
    disableBeacon: true,
    navigateTo: 'categories',
  },
  {
    target: '[data-tour="categories-prefix"]',
    title: 'Префикс артикула',
    content: (
      <div>
        <p className="mb-2">
          <strong>Префикс</strong> — это начальная часть артикула товара.
        </p>
        <p className="text-sm text-gray-600">
          Например, если префикс «КР», то артикулы товаров будут: КР-001, КР-002 и т.д.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    navigateTo: 'categories',
  },
  {
    target: '[data-tour="categories-create-btn"]',
    title: 'Создание категории',
    content: (
      <div>
        <p className="mb-2">
          Если нужной категории нет — создайте новую!
        </p>
        <p className="text-sm text-gray-600">
          При создании укажите все характеристики: название, префикс, состав, размеры и цены.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    navigateTo: 'categories',
  },

  // ==========================================
  // ЭТАП 4: ТОВАРЫ - Создание товара
  // ==========================================
  {
    target: 'body',
    title: 'Переходим к товарам',
    content: (
      <div>
        <p className="mb-2">
          Теперь самое важное — <strong>создание товаров</strong> на основе дизайна в выбранной категории.
        </p>
        <p className="text-sm text-gray-600">
          Товар связывает дизайн и категорию вместе.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    navigateTo: 'products',
  },
  {
    target: '[data-tour="products-create-btn"]',
    title: 'Создание товара',
    content: (
      <div>
        <p className="mb-2">
          Нажмите <strong>«Добавить товар»</strong>, чтобы создать новый товар.
        </p>
        <p className="text-sm text-gray-600">
          В форме укажите ID дизайна, выберите категорию — остальные данные подтянутся автоматически.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    navigateTo: 'products',
  },
  {
    target: '[data-tour="products-filter-status"]',
    title: 'Статусы товаров',
    content: (
      <div>
        <p className="mb-2">
          Товары имеют статусы валидации:
        </p>
        <div className="space-y-1 text-sm">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-800">Готов</span>
            <span>— можно выгружать</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">Есть проблемы</span>
            <span>— нужно исправить</span>
          </div>
        </div>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    navigateTo: 'products',
  },
  {
    target: '[data-tour="products-table"]',
    title: 'Выбор товаров для выгрузки',
    content: (
      <div>
        <p className="mb-2">
          Выберите товары, которые хотите выгрузить на маркетплейс.
        </p>
        <p className="text-sm text-gray-600">
          Используйте чекбоксы слева для выбора конкретных товаров или выберите все сразу.
        </p>
      </div>
    ),
    placement: 'top',
    disableBeacon: true,
    navigateTo: 'products',
  },
  {
    target: '[data-tour="products-bulk-actions"]',
    title: 'Массовые действия',
    content: (
      <div>
        <p className="mb-2">
          После выбора товаров появятся кнопки массовых действий:
        </p>
        <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
          <li><strong>Выгрузить на WB</strong> — отправить товары на Wildberries</li>
          <li><strong>Экспорт</strong> — скачать в Excel/CSV</li>
          <li><strong>Удалить</strong> — удалить выбранные товары</li>
        </ul>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    navigateTo: 'products',
  },

  // ==========================================
  // ЭТАП 5: ПУТИ ИЗОБРАЖЕНИЙ
  // ==========================================
  {
    target: 'body',
    title: 'Настройка путей изображений',
    content: (
      <div>
        <p className="mb-2">
          Перед выгрузкой на WB важно настроить <strong>пути к изображениям</strong>.
        </p>
        <p className="text-sm text-gray-600">
          Эта настройка определяет, какие фото будут загружены для каждого типа товара.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    navigateTo: 'media-mappings',
  },
  {
    target: '[data-tour="media-mappings-matrix"]',
    title: 'Матрица путей',
    content: (
      <div>
        <p className="mb-2">
          Матрица показывает настройки для каждой комбинации <strong>типа бумаги</strong> и <strong>типа дизайна</strong>.
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded p-2 text-sm">
          <p><strong>Пример:</strong></p>
          <p>Сахарная бумага + Круг → фото круглой печати на сахарной бумаге</p>
        </div>
      </div>
    ),
    placement: 'top',
    disableBeacon: true,
    navigateTo: 'media-mappings',
  },
  {
    target: '[data-tour="media-mappings-cell"]',
    title: 'Редактирование путей',
    content: (
      <div>
        <p className="mb-2">
          Кликните на ячейку, чтобы настроить пути к изображениям.
        </p>
        <p className="text-sm text-gray-600">
          Укажите шаблоны путей к фото на вашем сервере.
        </p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
    navigateTo: 'media-mappings',
  },
  {
    target: '[data-tour="media-mappings-import"]',
    title: 'Импорт стандартных путей',
    content: (
      <div>
        <p className="mb-2">
          Если у вас стандартная структура папок — импортируйте готовые настройки!
        </p>
        <p className="text-sm text-gray-600">
          Это сэкономит время на ручную настройку.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    navigateTo: 'media-mappings',
  },

  // ==========================================
  // ЭТАП 6: WILDBERRIES - Выгрузка
  // ==========================================
  {
    target: 'body',
    title: 'Финальный этап: Wildberries',
    content: (
      <div>
        <p className="mb-2">
          Всё готово для выгрузки! Переходим на страницу <strong>интеграции с Wildberries</strong>.
        </p>
        <div className="bg-green-50 border border-green-200 rounded p-2 text-sm">
          <p className="text-green-800">
            Здесь вы увидите товары, загруженные на WB, и сможете выгрузить новые.
          </p>
        </div>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    navigateTo: 'wildberries',
  },
  {
    target: '[data-tour="wb-seller-select"]',
    title: 'Выбор продавца',
    content: (
      <div>
        <p className="mb-2">
          Сначала выберите <strong>продавца</strong> (магазин), для которого будете работать.
        </p>
        <p className="text-sm text-gray-600">
          Каждый продавец имеет свой API-ключ и настройки.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    navigateTo: 'wildberries',
  },
  {
    target: '[data-tour="wb-sync-btn"]',
    title: 'Синхронизация с WB',
    content: (
      <div>
        <p className="mb-2">
          Кнопка <strong>«Синхронизировать»</strong> загружает актуальные данные о товарах с Wildberries.
        </p>
        <p className="text-sm text-gray-600">
          Рекомендуется синхронизировать перед выгрузкой новых товаров.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    navigateTo: 'wildberries',
  },
  {
    target: '[data-tour="wb-products-table"]',
    title: 'Товары на Wildberries',
    content: (
      <div>
        <p className="mb-2">
          Таблица показывает все товары, уже загруженные на WB.
        </p>
        <p className="text-sm text-gray-600">
          Статусы показывают текущее состояние каждого товара на маркетплейсе.
        </p>
      </div>
    ),
    placement: 'top',
    disableBeacon: true,
    navigateTo: 'wildberries',
  },
  {
    target: '[data-tour="wb-stats"]',
    title: 'Статистика',
    content: (
      <div>
        <p className="mb-2">
          Здесь отображается общая статистика по товарам на Wildberries.
        </p>
        <p className="text-sm text-gray-600">
          Количество товаров, активных карточек и другие показатели.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    navigateTo: 'wildberries',
  },

  // ==========================================
  // ЭТАП 7: ЗАВЕРШЕНИЕ
  // ==========================================
  {
    target: 'body',
    title: 'Поздравляем! 🎉',
    content: (
      <div>
        <p className="mb-3">
          Вы прошли полный цикл от создания дизайна до выгрузки на Wildberries!
        </p>
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3">
          <p className="font-medium text-indigo-800 mb-2">Краткое резюме:</p>
          <ol className="text-sm text-indigo-700 list-decimal pl-4 space-y-1">
            <li>Создайте дизайн → следите за комплектностью</li>
            <li>Выберите категорию для товаров</li>
            <li>Создайте товары, связав дизайн и категорию</li>
            <li>Настройте пути к изображениям</li>
            <li>Выгрузите товары на WB</li>
          </ol>
        </div>
        <p className="mt-3 text-sm text-gray-600">
          Теперь вы можете повторить этот процесс для любых дизайнов!
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    navigateTo: 'wildberries',
  },
];

export default fullCycleSteps;
